import { lazy, type ComponentType } from "react";
import { recarregarPorChunk, isChunkError } from "@/lib/chunkRecarrega";

/**
 * React.lazy que sobrevive a um deploy no meio da sessão.
 *
 * Cada build gera nomes de arquivo com hash próprio, e o container novo guarda
 * só os da versão imediatamente anterior (docker-publish.yml). Ainda falha
 * quem está com a aba aberta há dois deploys ou mais, e quem pede o chunk no
 * intervalo em que o container é trocado.
 *
 * O conserto é recarregar a página: o índice novo traz os nomes novos. O que
 * esta função evita é o caminho pelo qual isso passava antes — deixar a
 * exceção subir até o ErrorBoundary, que mostrava "Algo deu errado" e
 * registrava ocorrência crítica para o TI enquanto a página já estava indo
 * embora. Deploy não é defeito.
 *
 * Depois de mandar recarregar, a promessa fica pendente de propósito: a aba
 * está sendo trocada, e resolver ou rejeitar aqui só serviria para pintar tela
 * de erro nos milissegundos que faltam. O usuário continua vendo o "carregando".
 *
 * Se o recarregamento não resolveu (segunda falha na mesma janela curta), aí a
 * exceção sobe. Nesse caso a ocorrência tem valor: quer dizer que a recuperação
 * automática falhou, e não que houve um deploy.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkError(err)) throw err;
      // 2ª tentativa (falha de rede transitória)
      try {
        await new Promise((r) => setTimeout(r, 600));
        return await factory();
      } catch (err2) {
        if (!isChunkError(err2)) throw err2;
        if (recarregarPorChunk()) return new Promise<never>(() => {});
        throw err2;
      }
    }
  });
}
